import React, { useState, useEffect } from 'react';
import {
  BookOpen, Code, Cpu, Bot, Users, Mail, Phone, MapPin,
  ChevronDown, ArrowRight, Star, Award, Globe, Terminal,
  BookMarked, Lightbulb, Zap, UserCheck, PanelsTopLeft, BrainCircuit
} from 'lucide-react';
import { razorid } from '../exportsrazor';
import { useNavigate } from "react-router";


// Root level styling configuration
const styles = {
  colors: {
    primary: '#2563eb', // Royal blue - represents trust and professionalism
    secondary: '#7c3aed', // Purple - innovation and creativity
    accent: '#f59e0b', // Amber - energy and positivity
    text: {
      primary: '#1f2937', // Dark gray for main text
      secondary: '#4b5563', // Medium gray for secondary text
      light: '#9ca3af', // Light gray for tertiary text
    },
    background: {
      light: '#ffffff',
      subtle: '#f8fafc',
      gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    }
  },
  spacing: {
    section: 'py-24',
    container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  }
};

// Reusable components
const SectionTitle = ({ children, light, className = '' }) => (
  <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${light ? 'text-white' : 'text-gray-900'} ${className}`}>
    {children}
  </h2>
);

const SubTitle = ({ children, light, className = '' }) => (
  <p className={`text-xl md:text-2xl mb-12 ${light ? 'text-gray-200' : 'text-gray-600'} ${className}`}>
    {children}
  </p>
);

// Animated components
const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => setVisible(entry.isIntersecting));
    });

    const { current } = domRef;
    observer.observe(current);

    return () => observer.unobserve(current);
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Solutions', href: '#solutions' },
    { name: 'Products', href: '#products' },
    { name: 'Workshops', href: '#workshops' },
    // { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
      <div className={styles.spacing.container}>
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            {/* <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              CloudTales
            </span> */}
            <img src='/ctlogo.png' style={{ width: '81px' }} alt='Cloudtales Technologies' />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`${isScrolled ? 'text-grey-700' : 'text-white'
                  } hover:text-blue-600 font-medium transition-colors duration-300`}
              >
                {link.name}
              </a>
            ))}
            {/* <button className="bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors duration-300">
              Get Started
            </button> */}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-700"
          >
            <ChevronDown
              className={`w-6 h-6 transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''
                }`}
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

const Hero = () => (
  <div id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600">
    <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
    <div className={`${styles.spacing.container} relative z-10 text-center text-white`}>
      <FadeInSection>
        <h1 className="text-5xl md:text-7xl font-bold mb-8">
          Revolutionizing Digital Education
        </h1>
        <p className="text-xl md:text-2xl mb-12 text-gray-200 max-w-3xl mx-auto">
          Empowering the next generation with innovative learning solutions in computer science and digital technologies
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-colors duration-300">
            Explore Solutions
          </button>
          {/* <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-colors duration-300">
            Watch Demo
          </button> */}
        </div>
      </FadeInSection>
    </div>

    {/* Decorative elements */}
    <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-b from-transparent to-white/10"></div>
  </div>
);

const Features = () => {
  const features = [
    {
      icon: BookMarked,
      title: "Comprehensive Curriculum",
      description: "Structured learning paths from basic to advanced computer science concepts"
    },
    {
      icon: Lightbulb,
      title: "Interactive Learning",
      description: "Hands-on exercises and real-world projects for practical experience"
    },
    {
      icon: Zap,
      title: "Modern Technologies",
      description: "Stay updated with the latest trends in digital technology education"
    },
    {
      icon: UserCheck,
      title: "Expert Guidance",
      description: "Learn from industry professionals and experienced educators"
    }
  ];

  return (
    <section id="solutions" className={`${styles.spacing.section} bg-gray-50`}>
      <div className={styles.spacing.container}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <FadeInSection>
            <SectionTitle>Why Choose CloudTales?</SectionTitle>
            <SubTitle>
              We combine innovative teaching methods with cutting-edge technology to deliver exceptional learning experiences
            </SubTitle>
          </FadeInSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <FadeInSection key={index} delay={index * 100}>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-72">
                <feature.icon className="w-12 h-12 text-blue-600 mb-6" />
                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const BookCovers = ({ frontCover, backCover }) => {
  return (
    <div className="flex justify-center space-x-4 mb-6">
      <img
        src={frontCover}
        alt="Front Cover"
        className="w-32 h-48 object-cover rounded-lg shadow-md transform transition-transform hover:scale-105"
      />
      <img
        src={backCover}
        alt="Back Cover"
        className="w-32 h-48 object-cover rounded-lg shadow-md transform transition-transform hover:scale-105"
      />
    </div>
  );
};

const FormModal = ({ onSubmit, onClose, loading }) => {
  const [name, setName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [address, setAddress] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name, whatsappNumber, address });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: "999999" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6">
          <h2 className="text-2xl font-bold text-white">Enter Your Details</h2>
          <p className="text-sm text-blue-100 mt-1">
            We need your details to complete the purchase.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <div className="relative">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full text-black px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="Enter your name"
                required
              />
              <span className="absolute right-3 top-3 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
            <div className="relative">
              <input
                type="text"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="w-full text-black px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="+91 98765 43210"
                required
              />
              <span className="absolute right-3 top-3 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </span>
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <div className="relative">
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full text-black px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                rows="4"
                placeholder="123, Main Street, City, Country"
                required
              />
              <span className="absolute right-3 top-3 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing...
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Products = () => {
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const books = [
    {
      grade: 1,
      title: "Digital Discoveries: My First Computer Journey",
      description: "Introduction to computer basics and digital literacy",
      highlights: ["Basic computer operations", "Digital safety", "Introduction to coding"],
      frontCover: "/books/1.jpg",
      backCover: "/books/2.jpg",
      mrp: 699, // Maximum Retail Price
      discountedPrice: 499, // Discounted Price
    },
    {
      grade: 2,
      title: "Our Digital World: Exploring Life, Computers, and Technology",
      description: "Exploring creativity through technology",
      highlights: ["Block-based programming", "Digital art", "Simple animations"],
      frontCover: "/books/3.jpg",
      backCover: "/books/4.jpg",
      mrp: 699, // Maximum Retail Price
      discountedPrice: 499, // Discounted Price
    },
    {
      grade: 3,
      title: "Digital Explorers: Adventures in Technology and Creativity",
      description: "Creativity and 21st Century skills ",
      highlights: ["Basic Digital Literacy", "Creativity and Problem Solving", "Digital Citizenship and Health"],
      frontCover: "/books/5.jpg",
      backCover: "/books/6.jpg",
      mrp: 699, // Maximum Retail Price
      discountedPrice: 499, // Discounted Price
    },
    {
      grade: 4,
      title: "Digital Adventurers: Exploring the Past, Present, and Future of Technology",
      description: "Digital Adventurers: Exploring the Past, Present, and Future of Technology",
      highlights: ["Foundational Tech Skills", "Critical Thinking and Problem-Solving", "Digital Citizenship and Responsible Technology Use"],
      frontCover: "/books/7.jpg",
      backCover: "/books/8.jpg",
      mrp: 699, // Maximum Retail Price
      discountedPrice: 499, // Discounted Price
    },
  ];

  const handlePurchase = async (book) => {
    setSelectedBook(book);
    setShowForm(true);
  };
  const handleFormSubmit = async (userDetails) => {
    setLoading(true);

    try {
      // Call backend to create Razorpay order
      const response = await fetch('https://5pkt75v6ehib72gaikc7lhfxuy0osfmr.lambda-url.ap-south-1.on.aws/', {
        method: 'POST',
        body: JSON.stringify({ product: selectedBook, type: 'book', userDetails }),
      });
      const order = await response.json();

      // Open Razorpay checkout
      const options = {
        key: razorid.kyidlive,
        amount: order.amount,
        currency: "INR",
        name: selectedBook.title,
        description: selectedBook.description,
        order_id: order.id,
        handler: async (response) => {
          // Verify payment on the backend
          await fetch('https://omp7l2nzezptfxs35dq6pf7zwa0hfywg.lambda-url.ap-south-1.on.aws/', {
            method: 'POST',
            body: JSON.stringify({ ...response, product: selectedBook, type: 'book', userDetails }),
          });
          alert("Payment successful!");
          setLoading(false);
          setShowForm(false);
        },
        modal: {
          onclose: () => {
            // Handle modal close event (user cancels the transaction)
            // if (!loading) return; // Ensure this runs only if the modal is closed during processing
            setLoading(false);
            setShowForm(false);
            alert("Payment was cancelled.");
          },
          ondismiss: function () {
            setLoading(false);
            setShowForm(false);
            alert("Payment was cancelled.");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      setShowForm(false);
    }
  };


  return (
    <section id="products" className={`${styles.spacing.section} bg-white`}>
      <div className={styles.spacing.container}>
        <FadeInSection>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <SectionTitle>Educational Resources</SectionTitle>
            <SubTitle>
              Carefully crafted educational materials designed to make learning technology engaging and effective
            </SubTitle>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((book, index) => (
            <FadeInSection key={index} delay={index * 100}>
              <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 h-auto">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-3xl font-bold text-blue-600">Grade {book.grade}</span>
                  <BookOpen className="w-8 h-8 text-blue-600" />
                </div>
                <BookCovers frontCover={book.frontCover} backCover={book.backCover} />
                <h3 className="text-xl font-semibold mb-4">{book.title}</h3>
                <p className="text-gray-600 mb-6">{book.description}</p>
                <ul className="space-y-3 mb-6">
                  {book.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-center text-gray-700">
                      <Star className="w-4 h-4 text-amber-500 mr-2" />
                      {highlight}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-gray-500 text-lg strikethrough">₹{book.mrp}</span>
                  <span className="text-blue-600 text-2xl font-bold">₹{book.discountedPrice}</span>
                </div>
                <button
                  onClick={() => handlePurchase(book)}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Purchase Now
                </button>
              </div>
            </FadeInSection>
          ))}
        </div>

        {showForm && (
          <FormModal
            onSubmit={handleFormSubmit}
            onClose={() => setShowForm(false)}
            loading={loading}
          />
        )}
      </div>
    </section>
  );
};

const Workshops = () => {
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  const workshops = [
    {
      icon: Code,
      title: "Mobile Application Development",
      description: "Learn how a mobile app is created",
      duration: "8 weeks",
      level: "Beginner",
      mrp: 2499, // Maximum Retail Price
      discountedPrice: 1999, // Discounted Price
    },
    {
      icon: PanelsTopLeft,
      title: "Website Development",
      description: "Learn how a website is created",
      duration: "8 weeks",
      level: "Beginner",
      mrp: 2499, // Maximum Retail Price
      discountedPrice: 1999, // Discounted Price
    },
    {
      icon: Cpu,
      title: "Electronics",
      description: "Learn about basic knowledge of electronics and components",
      duration: "4 weeks",
      level: "Beginner",
      mrp: 2499, // Maximum Retail Price
      discountedPrice: 1999, // Discounted Price
    },
    {
      icon: Terminal,
      title: "Python",
      description: "Learn to code in Python",
      duration: "8 weeks",
      level: "Intermediate",
      mrp: 8999, // Maximum Retail Price
      discountedPrice: 8000, // Discounted Price
    },
    {
      icon: BrainCircuit,
      title: "Artificial Intelligence",
      description: "Learn to use AI",
      duration: "4 weeks",
      level: "Intermediate",
      mrp: 6999, // Maximum Retail Price
      discountedPrice: 6000, // Discounted Price
    },
    {
      icon: Bot,
      title: "Robotics",
      description: "Learn Robotics",
      duration: "12 weeks",
      level: "Beginner",
      mrp: 6999, // Maximum Retail Price
      discountedPrice: 2500, // Discounted Price
    },
  ];

  const handlePurchase = async (workshop) => {
    setSelectedWorkshop(workshop);
    setShowForm(true);
  };

  const handleFormSubmit = async (userDetails) => {
    setLoading(true);

    try {
      // Call backend to create Razorpay order
      const response = await fetch('https://5pkt75v6ehib72gaikc7lhfxuy0osfmr.lambda-url.ap-south-1.on.aws/', {
        method: 'POST',
        body: JSON.stringify({ product: selectedWorkshop, type: 'workshop', userDetails }),
      });
      const order = await response.json();

      // Open Razorpay checkout
      const options = {
        key: razorid.kyidlive,
        amount: order.amount,
        currency: "INR",
        name: selectedWorkshop.title,
        description: selectedWorkshop.description,
        order_id: order.id,
        handler: async (response) => {
          // Verify payment on the backend
          await fetch('https://omp7l2nzezptfxs35dq6pf7zwa0hfywg.lambda-url.ap-south-1.on.aws/', {
            method: 'POST',
            body: JSON.stringify({ ...response, product: selectedWorkshop, type: 'workshop', userDetails }),
          });
          alert("Payment successful!");
          setLoading(false);
          setShowForm(false);
        },
        modal: {
          onclose: () => {
            // Handle modal close event (user cancels the transaction)
            // if (!loading) return; // Ensure this runs only if the modal is closed during processing
            setLoading(false);
            setShowForm(false);
            alert("Payment was cancelled.");
          },
          ondismiss: function () {
            setLoading(false);
            setShowForm(false);
            alert("Payment was cancelled.");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      setShowForm(false);
    }
  };

  return (
    <section id="workshops" className={`${styles.spacing.section} bg-gradient-to-r from-blue-600 to-purple-600 text-white`}>
      <div className={styles.spacing.container}>
        <FadeInSection>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <SectionTitle light>Hands-on Workshops</SectionTitle>
            <SubTitle light>
              Immersive learning experiences that combine theory with practical application
            </SubTitle>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {workshops.map((workshop, index) => (
            <FadeInSection key={index} delay={index * 100}>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 transition-colors duration-300 h-auto">
                <workshop.icon className="w-12 h-12 text-white mb-6" />
                <h3 className="text-xl font-semibold mb-4">{workshop.title}</h3>
                <p className="text-gray-200 mb-6">{workshop.description}</p>
                <div className="flex items-center justify-between text-sm mb-6">
                  <span className="bg-white/20 px-3 py-1 rounded-full">
                    {workshop.duration}
                  </span>
                  <span className="bg-white/20 px-3 py-1 rounded-full">
                    {workshop.level}
                  </span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-gray-300 text-lg strikethrough">₹{workshop.mrp}</span>
                  <span className="text-white text-2xl font-bold">₹{workshop.discountedPrice}</span>
                </div>
                {workshop.title === "Robotics" ?
                  <>
                    <button
                      onClick={() => navigate("/robotics")}
                      className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
                    >
                      View
                    </button>
                  </> :
                  <button
                    onClick={() => handlePurchase(workshop)}
                    className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
                  >
                    Enroll Now
                  </button>
                }
              </div>
            </FadeInSection>
          ))}
        </div>

        {showForm && (
          <FormModal
            onSubmit={handleFormSubmit}
            onClose={() => setShowForm(false)}
            loading={loading}
          />
        )}
      </div>
    </section>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      name: "Nisha Mohandas",
      role: "Committee Member, Ramji Assar Trust",
      content: "Our school has been incredibly fortunate to collaborate with your team for our computer period. The tech education program you’ve implemented has been transformative for our students. They are not only learning the basics of computers but are also being introduced to advanced concepts like coding and digital literacy. The structured curriculum and engaging teaching methods have made a significant impact, and we’ve seen a noticeable improvement in the students' confidence and skills. Thank you for empowering our children with the tools they need to succeed in a tech-driven world. We look forward to continuing this partnership!",
      image: "/api/placeholder/64/64"
    },
    {
      name: "Ajay",
      role: "Student",
      content: "I joined the mobile app development workshop, and it has been one of the best learning experiences for me. Before this, I had no idea how apps were made, but now I can confidently create basic apps on my own. The instructors were patient and explained everything step by step, making it easy to understand. I also learned about problem-solving and teamwork during the project work. This workshop has inspired me to explore more in the field of technology. Thank you for giving me this opportunity!",
      image: "/api/placeholder/64/64"
    },
    {
      name: "Chandan Singh",
      role: "Cofounder, Pratigya Foundation",
      content: "Partnering with your team to implement tech workshops has been a game-changer for our foundation. The workshops you conducted for underprivileged youth were not only educational but also highly motivating. The hands-on approach to teaching coding, digital tools, and tech skills has opened up new opportunities for the participants. Many of them have expressed how these workshops have boosted their confidence and given them a sense of direction. Your dedication to making tech accessible to all is truly commendable. We are excited to continue working together to create more impactful programs!",
      image: "/api/placeholder/64/64"
    }
  ];

  return (
    <section className={`${styles.spacing.section} bg-gray-50`}>
      <div className={styles.spacing.container}>
        <FadeInSection>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <SectionTitle>What People Say</SectionTitle>
            <SubTitle>
              Don't just take our word for it - hear from our community
            </SubTitle>
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <FadeInSection key={index} delay={index * 100}>
              <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-6">
                  {/* <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  /> */}
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">{testimonial.content}</p>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => (
  <section id="contact" className={`${styles.spacing.section} bg-white`}>
    <div className={styles.spacing.container}>
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-16">
        <FadeInSection>
          <div>
            <SectionTitle>Get in Touch</SectionTitle>
            <SubTitle>
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </SubTitle>

            <div className="space-y-6">
              <div className="flex items-center">
                <Mail className="w-6 h-6 text-blue-600 mr-4" />
                <span className="text-gray-700">info@cloudtales.in</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-6 h-6 text-blue-600 mr-4" />
                <span className="text-gray-700">+91 9819044922</span>
              </div>
              <div className="flex items-center">
                <MapPin className="w-6 h-6 text-blue-600 mr-4" />
                <span className="text-gray-700">G.B. Dutia Information and Technology school, Ghatkopar, Mumbai</span>
              </div>
              <div className="flex items-center">
                <MapPin className="w-6 h-6 text-blue-600 mr-4" />
                <span className="text-gray-700">Nashik Enginerring Cluster, Ambad, Nashik</span>
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* <FadeInSection delay={200}>
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                type="text"
                placeholder="First Name"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-300"
              />
              <input
                type="text"
                placeholder="Last Name"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-300"
              />
            </div>
            <input
              type="email"
              placeholder="Email Address"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-300"
            />
            <textarea
              placeholder="Your Message"
              rows="6"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-300"
            ></textarea>
            <button className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300">
              Send Message
            </button>
          </form>
        </FadeInSection> */}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-gray-900 text-white">
    <div className={`${styles.spacing.container} pt-16 pb-8`}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12 mb-12">
        <div>
          <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            CloudTales
          </h3>
          <p className="text-gray-400 mb-6">
            Empowering the next generation through innovative digital education solutions.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
          <ul className="space-y-4">
            <li><a href="#products" className="text-gray-400 hover:text-white transition-colors duration-300">Our Products</a></li>
            <li><a href="#workshops" className="text-gray-400 hover:text-white transition-colors duration-300">Workshops</a></li>
            <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors duration-300">Contact</a></li>
          </ul>
        </div>

        {/* <div>
          <h4 className="text-lg font-semibold mb-6">Resources</h4>
          <ul className="space-y-4">
            <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Blog</a></li>
            <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Documentation</a></li>
            <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Support Center</a></li>
            <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Privacy Policy</a></li>
          </ul>
        </div> */}

        {/* <div>
          <h4 className="text-lg font-semibold mb-6">Newsletter</h4>
          <p className="text-gray-400 mb-4">Stay updated with our latest news and updates</p>
          <div className="flex">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 rounded-l-lg bg-gray-800 border border-gray-700 focus:outline-none focus:border-blue-500"
            />
            <button className="px-6 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors duration-300">
              Subscribe
            </button>
          </div>
        </div> */}
      </div>

      <div className="border-t border-gray-800 pt-8 text-center">
        <p className="text-gray-400">&copy; 2025 CloudTales Technologies. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

const App = () => {
  return (
    <div className="relative">
      <Navigation />
      <main>
        <Hero />
        <Features />
        <Products />
        <Workshops />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default App;